/**
 * render/combat_ui.ts — When the World Bites Back (Visually)
 *
 * All combat-related overlays: player health bar, creature health bar,
 * floating damage numbers, combat status indicator, and level-up flash.
 *
 * Follows the warm wood/gold palette established in hud.ts. Combat should
 * feel tense but never alien — these are still hand-crafted panels and
 * warm amber glows, not sci-fi HUDs dropped from another game.
 *
 * Called every frame by the room renderer when the player is in a danger zone
 * or has active combat state. Hot path — zero allocations where possible,
 * pre-computed gradients, no string concatenation in tight loops.
 */

import type { GameState, DamageNumber } from "../engine/state.js";
import type { Camera } from "../iso/projection.js";
import {
  getCreatureHp, getMomentumState, getAttunementData,
  drainCombatXpEvent, peekCombatXpEvent,
} from "../combat/system.js";
import { drawEnhancedDamageNumber } from "../render/ability_ui.js";
import { getSpecies } from "../creature/species.js";
import {
  createMomentumTelemetry, drawMomentumMeter,
  type MomentumTelemetry,
} from "./momentum_ui.js";

// ─── Palette ────────────────────────────────────────────────────────────────
// Matches the warm aged-wood aesthetic from hud.ts.

const C = {
  // Wood panel
  woodDark:   "#1e1508",
  woodMid:    "#2a1d0e",
  woodLight:  "#342414",
  // Gold accents
  goldDark:   "#6b4f0c",
  goldMid:    "#9b7318",
  goldBright: "#d4a030",
  goldGlow:   "#f0c860",
  // Text
  textPrime:  "#f0d88a",
  textDim:    "#c0a060",
  // Health bar fills
  hpFull:     "#4a9a40",    // green at 100%
  hpMid:      "#c8a020",    // gold-yellow at ~50%
  hpLow:      "#9a3020",    // deep red at 0%
  hpBg:       "#0e0a04",    // dark inset behind bar
  hpBorder:   "#3a2810",
  // XP bar
  xpFill:     "#c8a050",
  xpBg:       "#120e06",
  // Creature bar
  creatureHpHi: "#c83030",
  creatureHpLo: "#4a1010",
  // Damage numbers
  dmgPlayer:  "#e84040",    // red — player took damage
  dmgEnemy:   "#f0e8d0",    // warm white — creature took damage
  dmgCrit:    "#ff8820",    // orange — critical hit
  dmgCritGlow:"#ffcc44",
  // Combat status
  combatText: "#e8a060",
  combatGlow: "rgba(232,160,96,0.25)",
  // Level up
  levelUpText:"#f0c860",
  levelUpGlow:"rgba(240,200,96,0.5)",
  // Equipment strip
  slotBg:     "#0c0804",
  slotBorder: "#6b4f0c",
  slotEmpty:  "#2a2018",
  // Rarity colors
  rarCommon:  "#b8b0a0",
  rarUncommon:"#48a848",
  rarRare:    "#4888d8",
  rarLegendary:"#a050d0",
  // Stat readouts
  atkColor:   "#d4a030",
  defColor:   "#88a8c8",
  // Combo counter
  comboGold:  "#d4a030",
  comboOrange:"#e87820",
  comboRed:   "#e83830",
  // Danger stars
  starColor:  "#d4a030",
  starDim:    "#3a2810",
} as const;

// ─── Layout Constants ───────────────────────────────────────────────────────

const PANEL_X       = 16;
const PANEL_Y       = 108;   // below compass (radius 42 at y=16 → bottom at y=100 + 8px gap)
const PANEL_W       = 200;
const PANEL_H       = 82;
const PANEL_R       = 6;      // border radius

const HP_BAR_X      = 10;
const HP_BAR_Y      = 24;
const HP_BAR_W      = 158;
const HP_BAR_H      = 14;

const EP_BAR_Y      = 42;
const EP_BAR_H      = 6;

const XP_BAR_Y      = 52;
const XP_BAR_H      = 6;

const LVL_X         = 172;
const LVL_Y         = 18;

const CREATURE_BAR_W = 48;
const CREATURE_BAR_H = 5;

// Equipment quick-view strip
const EQ_STRIP_X    = PANEL_X;
const EQ_STRIP_Y    = PANEL_Y + PANEL_H + 4;
const EQ_STRIP_W    = 200;
const EQ_STRIP_H    = 28;
const EQ_SLOT_SIZE  = 22;
const EQ_SLOT_GAP   = 4;
const EQ_SLOT_Y_OFF = 3;  // vertical centering within strip

// ─── Timing ─────────────────────────────────────────────────────────────────

const DAMAGE_LIFETIME  = 1.2;   // seconds (must match ability_ui LIFETIME)
const DAMAGE_FADE_IN   = 0.5;   // seconds before end where fading starts
const DAMAGE_RISE_PX   = 40;    // total pixels the number floats upward
const LEVELUP_DURATION = 3.0;   // seconds the "Level Up!" flash persists

// ─── Level Up State ─────────────────────────────────────────────────────────
// Exported so the combat system can trigger it from applyLevelUp.

let levelUpFlashTime = 0;
let lastKnownLevel   = 0;

// ─── Smooth HP Bar State ───────────────────────────────────────────────────
// displayHp lerps toward actual HP each frame for buttery-smooth bar movement.
// ghostHp captures the old value when HP drops, then fades out over 0.5s — the
// Elden Ring / Smash Bros "afterimage drain" that makes every hit readable.

let displayHp      = -1;  // -1 = uninitialized, snaps to actual on first frame
let ghostHp        = -1;  // ghost bar HP value (lingers at old position)
let ghostHpAlpha   = 0;   // current opacity of the ghost bar (fades over time)
let ghostHpStart   = 0;   // timestamp when ghost bar was spawned
const GHOST_HP_DURATION = 0.5; // seconds for ghost bar to fade out
let lastFrameTime  = 0;   // previous frame timestamp for dt calculation

// ─── XP Glow State ─────────────────────────────────────────────────────────
// Tracks XP changes to drive shimmer, glow, and floating "+X XP" numbers.

let previousXp: number         = -1; // -1 = uninitialized
let previousLevel: number      = -1; // tracks level to detect level-ups
let xpGainGlowStart: number   = 0;  // timestamp when glow began
let xpGainOldRatio: number     = 0;  // XP bar ratio before the gain (for shimmer region)
const XP_GLOW_DURATION         = 0.8; // seconds for shimmer to fade

/** Floating XP gain number — mirrors the damage number pattern but in gold. */
interface XpFloatNumber {
  value: number;
  createdAt: number;
  /** Optional novelty multiplier label — when set, rendered beside the XP value */
  noveltyMult?: number;
}
const xpFloatNumbers: XpFloatNumber[] = [];

/** Momentum meter telemetry — ring buffer of recent values for the heartbeat trace */
const _momentumTel: MomentumTelemetry = createMomentumTelemetry();

/** Call from outside to trigger the level-up flash animation. */
export function triggerLevelUpFlash(): void {
  levelUpFlashTime = performance.now() / 1000;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Get current zone danger level from world state. */
function getDangerLevel(state: GameState): number {
  const zoneId = state.world?.currentZoneId;
  if (!zoneId || !state.world?.zones) return 0;
  const zones = state.world.zones;
  for (let i = 0; i < zones.length; i++) {
    if (zones[i].id === zoneId) return zones[i].dangerLevel;
  }
  return 0;
}

/** Project tile coords through camera to canvas pixels. */
function toScreen(
  tileX: number,
  tileY: number,
  elevation: number,
  camera: Camera,
  _cw: number,
  _ch: number,
): { x: number; y: number } {
  const wx = tileX + 0.5;
  const wy = tileY + 0.5;
  const sx = (wx - wy) * 32;
  const sy = (wx + wy) * 16 - elevation * 16;
  // Must match applyCamera convention: camera.x/y already include canvas center offset
  return {
    x: sx * camera.zoom + camera.x,
    y: sy * camera.zoom + camera.y,
  };
}

/** Linearly interpolate between two values. */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Clamp a value between min and max. */
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Interpolate an HP color: green → yellow → red as ratio drops from 1 → 0. */
function hpColor(ratio: number): string {
  // ratio 1.0 → green, 0.5 → yellow, 0.0 → red
  const r = ratio > 0.5
    ? Math.round(lerp(200, 74, (ratio - 0.5) * 2))
    : Math.round(lerp(154, 200, ratio * 2));
  const g = ratio > 0.5
    ? Math.round(lerp(160, 154, (ratio - 0.5) * 2))
    : Math.round(lerp(32, 160, ratio * 2));
  const b = ratio > 0.5
    ? Math.round(lerp(32, 64, (ratio - 0.5) * 2))
    : Math.round(lerp(32, 32, ratio * 2));
  return `rgb(${r},${g},${b})`;
}

// ─── Rounded Rect Helper ────────────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

// ─── Wood Panel Background ──────────────────────────────────────────────────

function drawWoodPanel(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  // Dark fill
  roundRect(ctx, x, y, w, h, r);
  ctx.fillStyle = C.woodDark;
  ctx.fill();

  // Subtle inner highlight along top edge
  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.clip();
  const innerG = ctx.createLinearGradient(x, y, x, y + 8);
  innerG.addColorStop(0, "rgba(52,36,20,0.6)");
  innerG.addColorStop(1, "transparent");
  ctx.fillStyle = innerG;
  ctx.fillRect(x, y, w, 8);
  ctx.restore();

  // Gold border
  roundRect(ctx, x, y, w, h, r);
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1.5;
  ctx.stroke();
}

// ─── Player Health Bar ──────────────────────────────────────────────────────

function drawPlayerHealthBar(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const { combat, combatState } = state.player;

  // Wood panel background
  drawWoodPanel(ctx, PANEL_X, PANEL_Y, PANEL_W, PANEL_H, PANEL_R);

  // ── Level badge (top-right of panel) ────────────────────────────────
  ctx.save();
  ctx.font = "bold 11px monospace";
  ctx.fillStyle = C.goldBright;
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.fillText(`Lv. ${combat.level}`, PANEL_X + LVL_X, PANEL_Y + 6);
  ctx.restore();

  // ── HP label ────────────────────────────────────────────────────────
  ctx.save();
  ctx.font = "bold 11px monospace";
  ctx.fillStyle = C.textPrime;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("HP", PANEL_X + HP_BAR_X, PANEL_Y + 8);
  ctx.restore();

  // ── HP numeric ──────────────────────────────────────────────────────
  ctx.save();
  ctx.font = "10px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(`${combat.hp}/${combat.maxHp}`, PANEL_X + HP_BAR_X + 22, PANEL_Y + 9);
  ctx.restore();

  // ── HP bar background (inset) ───────────────────────────────────────
  const bx = PANEL_X + HP_BAR_X;
  const by = PANEL_Y + HP_BAR_Y;

  roundRect(ctx, bx, by, HP_BAR_W, HP_BAR_H, 3);
  ctx.fillStyle = C.hpBg;
  ctx.fill();

  // ── HP bar fill (smooth animation + ghost bar) ──────────────────────
  const actualRatio = clamp(combat.hp / combat.maxHp, 0, 1);

  // Compute frame delta for smooth lerp
  const dt = lastFrameTime > 0 ? Math.max(0, now - lastFrameTime) : 0;

  // Initialize displayHp on first frame; thereafter lerp toward actual
  if (displayHp < 0) {
    displayHp = combat.hp;
  } else {
    const prevDisplay = displayHp;
    displayHp = displayHp + (combat.hp - displayHp) * Math.min(1, dt * 8);

    // When HP drops, spawn or extend ghost bar at the old display position.
    // If a ghost is already visible, anchor to whichever is higher so rapid
    // consecutive hits don't silently swallow the afterimage feedback.
    if (combat.hp < prevDisplay - 0.1) {
      ghostHp = ghostHpAlpha > 0.01 ? Math.max(ghostHp, prevDisplay) : prevDisplay;
      ghostHpAlpha = 1;
      ghostHpStart = now;
    }
  }

  // Fade ghost bar over time
  if (ghostHpAlpha > 0 && ghostHpStart > 0) {
    const ghostAge = now - ghostHpStart;
    ghostHpAlpha = ghostAge < GHOST_HP_DURATION
      ? 1 - ghostAge / GHOST_HP_DURATION
      : 0;
  }

  const ratio = clamp(displayHp / combat.maxHp, 0, 1);
  const fillW = Math.round(HP_BAR_W * ratio);

  // ── Ghost bar — afterimage at old HP position (drawn first, behind fill) ──
  if (ghostHpAlpha > 0.01 && ghostHp > displayHp) {
    const ghostRatio = clamp(ghostHp / combat.maxHp, 0, 1);
    const ghostFillW = Math.round(HP_BAR_W * ghostRatio);
    if (ghostFillW > fillW) {
      ctx.save();
      roundRect(ctx, bx, by, HP_BAR_W, HP_BAR_H, 3);
      ctx.clip();
      ctx.globalAlpha = ghostHpAlpha * 0.45;
      // Lighter, desaturated red-amber for the ghost portion
      ctx.fillStyle = "#c85838";
      ctx.fillRect(bx + fillW, by, ghostFillW - fillW, HP_BAR_H);
      ctx.restore();
    }
  }

  if (fillW > 0) {
    ctx.save();
    roundRect(ctx, bx, by, HP_BAR_W, HP_BAR_H, 3);
    ctx.clip();

    const fillG = ctx.createLinearGradient(bx, by, bx + HP_BAR_W, by);
    fillG.addColorStop(0, hpColor(ratio));
    fillG.addColorStop(1, hpColor(Math.max(0, ratio - 0.15)));
    ctx.fillStyle = fillG;
    ctx.fillRect(bx, by, fillW, HP_BAR_H);

    // Specular highlight along top half
    const specG = ctx.createLinearGradient(bx, by, bx, by + HP_BAR_H * 0.5);
    specG.addColorStop(0, "rgba(255,255,255,0.12)");
    specG.addColorStop(1, "transparent");
    ctx.fillStyle = specG;
    ctx.fillRect(bx, by, fillW, HP_BAR_H * 0.5);

    ctx.restore();
  }

  // ── HP bar border ───────────────────────────────────────────────────
  roundRect(ctx, bx, by, HP_BAR_W, HP_BAR_H, 3);
  ctx.strokeStyle = C.hpBorder;
  ctx.lineWidth = 1;
  ctx.stroke();

  // ── EP bar (Ember Points, between HP and XP) ───────────────────
  const ebx = bx;
  const eby = PANEL_Y + EP_BAR_Y;
  const ember = combat.ember;

  // Background inset
  roundRect(ctx, ebx, eby, HP_BAR_W, EP_BAR_H, 2);
  ctx.fillStyle = C.hpBg;
  ctx.fill();

  // EP fill — amber/fire gradient, shifts darker with red tint below 30%
  const epRatio = ember.max > 0 ? clamp(ember.current / ember.max, 0, 1) : 0;
  const epFillW = Math.round(HP_BAR_W * epRatio);

  if (epFillW > 0) {
    ctx.save();
    roundRect(ctx, ebx, eby, HP_BAR_W, EP_BAR_H, 2);
    ctx.clip();

    const epLow = epRatio < 0.3;
    const epG = ctx.createLinearGradient(ebx, eby, ebx + HP_BAR_W, eby);
    if (epLow) {
      // Below 30%: darker amber with red tint
      epG.addColorStop(0, "#a04818");
      epG.addColorStop(1, "#8a3020");
    } else {
      epG.addColorStop(0, "#e88020");
      epG.addColorStop(1, "#d4a030");
    }
    ctx.fillStyle = epG;
    ctx.fillRect(ebx, eby, epFillW, EP_BAR_H);

    // Specular
    const epSpecG = ctx.createLinearGradient(ebx, eby, ebx, eby + EP_BAR_H * 0.5);
    epSpecG.addColorStop(0, "rgba(255,255,255,0.10)");
    epSpecG.addColorStop(1, "transparent");
    ctx.fillStyle = epSpecG;
    ctx.fillRect(ebx, eby, epFillW, EP_BAR_H * 0.5);

    // Regeneration pulse glow — subtle shimmer when EP is regenerating
    const isRegenerating = ember.current < ember.max;
    if (isRegenerating) {
      const regenPulse = 0.08 + Math.sin(now * Math.PI * 3) * 0.06;
      ctx.globalAlpha = regenPulse;
      ctx.fillStyle = "#f0c860";
      ctx.fillRect(ebx, eby, epFillW, EP_BAR_H);
      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }

  // EP border
  roundRect(ctx, ebx, eby, HP_BAR_W, EP_BAR_H, 2);
  ctx.strokeStyle = C.hpBorder;
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // EP text
  ctx.save();
  ctx.font = "8px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(`EP ${Math.floor(ember.current)}/${ember.max}`, ebx, eby + EP_BAR_H + 1);
  ctx.restore();

  // ── XP bar (thin, underneath EP bar) — with glow and shimmer ────────
  const xbx = bx;
  const xby = PANEL_Y + XP_BAR_Y;

  roundRect(ctx, xbx, xby, HP_BAR_W, XP_BAR_H, 2);
  ctx.fillStyle = C.xpBg;
  ctx.fill();

  const xpRatio = combat.xpToNext > 0
    ? clamp(combat.xp / combat.xpToNext, 0, 1)
    : 0;
  const xpFillW = Math.round(HP_BAR_W * xpRatio);

  // Detect XP changes for shimmer and floating numbers
  if (previousXp < 0) {
    previousXp = combat.xp;
    previousLevel = combat.level;
  } else if (combat.xp !== previousXp || combat.level !== previousLevel) {
    const leveled = combat.level > previousLevel;
    if (leveled) {
      // Level-up: XP reset to 0 — shimmer the entire bar from 0, show gained XP
      xpGainOldRatio = 0;
      xpGainGlowStart = now;
      if (combat.xp > 0) {
        // Drain any pending combat XP event so novelty mult is preserved
        const pending = drainCombatXpEvent();
        xpFloatNumbers.push({
          value: combat.xp,
          createdAt: now,
          noveltyMult: pending?.noveltyMult,
        });
      }
    } else {
      const gained = combat.xp - previousXp;
      if (gained > 0) {
        // Record old ratio for shimmer region
        xpGainOldRatio = combat.xpToNext > 0
          ? clamp(previousXp / combat.xpToNext, 0, 1)
          : 0;
        xpGainGlowStart = now;
        // If there's a pending combat XP event matching this gain, drain it
        // and tag the float with the novelty multiplier. Otherwise emit a
        // generic float (non-combat XP sources — quests, skills, etc).
        const peek = peekCombatXpEvent();
        if (peek && Math.abs(peek.value - gained) <= 1) {
          const pending = drainCombatXpEvent()!;
          xpFloatNumbers.push({
            value: gained,
            createdAt: now,
            noveltyMult: pending.noveltyMult,
          });
        } else {
          xpFloatNumbers.push({ value: gained, createdAt: now });
        }
      }
    }
    previousXp = combat.xp;
    previousLevel = combat.level;
  }

  // Shimmer/glow age
  const xpGlowAge = xpGainGlowStart > 0 ? now - xpGainGlowStart : XP_GLOW_DURATION + 1;
  const xpGlowActive = xpGlowAge < XP_GLOW_DURATION;
  const xpGlowT = xpGlowActive ? xpGlowAge / XP_GLOW_DURATION : 1;

  if (xpFillW > 0) {
    ctx.save();
    roundRect(ctx, xbx, xby, HP_BAR_W, XP_BAR_H, 2);
    ctx.clip();

    // Base fill
    ctx.fillStyle = C.xpFill;
    ctx.fillRect(xbx, xby, xpFillW, XP_BAR_H);

    // Shimmer on newly gained portion — bright gold that fades over 0.8s
    if (xpGlowActive) {
      const shimmerAlpha = 0.4 * (1 - xpGlowT);
      const shimmerStartX = xbx + Math.round(HP_BAR_W * xpGainOldRatio);
      const shimmerEndX = xbx + xpFillW;
      if (shimmerEndX > shimmerStartX) {
        ctx.globalAlpha = shimmerAlpha;
        ctx.fillStyle = "#f0d88a";
        ctx.fillRect(shimmerStartX, xby, shimmerEndX - shimmerStartX, XP_BAR_H);
        ctx.globalAlpha = 1;
      }
    }

    ctx.restore();
  }

  // Full-bar glow pulse when at 100% (about to level up)
  if (xpRatio >= 0.999) {
    ctx.save();
    roundRect(ctx, xbx, xby, HP_BAR_W, XP_BAR_H, 2);
    ctx.clip();
    const fullBarPulse = 0.12 + Math.sin(now * Math.PI * 4) * 0.08; // 2Hz pulse
    ctx.globalAlpha = fullBarPulse;
    ctx.fillStyle = C.goldGlow;
    ctx.fillRect(xbx, xby, HP_BAR_W, XP_BAR_H);
    ctx.restore();
  }

  roundRect(ctx, xbx, xby, HP_BAR_W, XP_BAR_H, 2);
  ctx.strokeStyle = C.hpBorder;
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // ── XP text ─────────────────────────────────────────────────────────
  ctx.save();
  ctx.font = "9px monospace";
  ctx.fillStyle = C.textDim;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(`XP ${combat.xp}/${combat.xpToNext}`, xbx, xby + XP_BAR_H + 2);
  ctx.restore();

  // ── Floating "+X XP" numbers ────────────────────────────────────────
  drawXpFloatNumbers(ctx, xbx, xby, now);

  // ── Combat status indicator ─────────────────────────────────────────
  if (combatState.active) {
    drawCombatIndicator(ctx, PANEL_X + PANEL_W + 8, PANEL_Y + 6, now);
  }
}

// ─── Floating XP Numbers ───────────────────────────────────────────────────
// Gold "+X XP" text that rises above the XP bar when experience is gained.
// Mirrors the damage number pattern with the warm gold palette.

const XP_FLOAT_LIFETIME = 1.2; // seconds
const XP_FLOAT_RISE     = 28;  // pixels upward

function drawXpFloatNumbers(
  ctx: CanvasRenderingContext2D,
  barX: number,
  barY: number,
  now: number,
): void {
  // Clean expired entries
  for (let i = xpFloatNumbers.length - 1; i >= 0; i--) {
    if (now - xpFloatNumbers[i].createdAt >= XP_FLOAT_LIFETIME) {
      xpFloatNumbers.splice(i, 1);
    }
  }
  if (xpFloatNumbers.length === 0) return;

  ctx.save();
  for (let i = 0; i < xpFloatNumbers.length; i++) {
    const xn = xpFloatNumbers[i];
    const age = now - xn.createdAt;
    if (age >= XP_FLOAT_LIFETIME) continue;

    const t = age / XP_FLOAT_LIFETIME;
    const rise = t * XP_FLOAT_RISE;

    // Fade: instant onset, fade out in last 40%
    const fadeStart = 0.6;
    const alpha = t > fadeStart ? 1 - (t - fadeStart) / (1 - fadeStart) : 1;

    // Position above center of XP bar
    const sx = barX + HP_BAR_W * 0.5;
    const sy = barY - 4 - rise;

    ctx.globalAlpha = clamp(alpha, 0, 1);

    // Primary "+X XP" label
    ctx.font = "bold 12px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Text shadow
    ctx.fillStyle = "rgba(0,0,0,0.6)";
    ctx.fillText(`+${xn.value} XP`, sx + 1, sy + 1);

    // Color and glow respond to novelty: fresh kills blaze hotter, familiar
    // kills render in a dimmer, more reserved gold to surface the decay.
    let xpColor: string = C.goldGlow;
    let glowColor: string = "rgba(240,200,96,0.5)";
    let noveltyLabel: string | null = null;
    let noveltyColor: string | null = null;

    if (xn.noveltyMult != null) {
      const m = xn.noveltyMult;
      if (m >= 1.3) {
        xpColor = "#ffe890";             // blazing gold-white for novel encounters
        glowColor = "rgba(255,232,144,0.7)";
        noveltyLabel = `×${m.toFixed(2)} NOVEL!`;
        noveltyColor = "#ffe890";
      } else if (m >= 0.9) {
        xpColor = C.goldGlow;            // default
        glowColor = "rgba(240,200,96,0.5)";
        noveltyLabel = `×${m.toFixed(2)}`;
        noveltyColor = C.textDim;
      } else if (m >= 0.4) {
        xpColor = "#d0a050";             // dimming as familiarity grows
        glowColor = "rgba(208,160,80,0.35)";
        noveltyLabel = `×${m.toFixed(2)} familiar`;
        noveltyColor = "#a88848";
      } else {
        xpColor = "#a88848";             // deeply familiar — minimal shine
        glowColor = "rgba(168,136,72,0.25)";
        noveltyLabel = `×${m.toFixed(2)} routine`;
        noveltyColor = "#705830";
      }
    }

    ctx.shadowColor = glowColor;
    ctx.shadowBlur = 6;
    ctx.fillStyle = xpColor;
    ctx.fillText(`+${xn.value} XP`, sx, sy);
    ctx.shadowBlur = 0;

    // Secondary novelty label — smaller, trailing beneath the XP value
    if (noveltyLabel && noveltyColor) {
      ctx.font = "bold 9px monospace";
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.fillText(noveltyLabel, sx + 1, sy + 11);
      ctx.fillStyle = noveltyColor;
      ctx.fillText(noveltyLabel, sx, sy + 10);
    }
  }
  ctx.restore();
}

// ─── Low Health Warning Vignette ───────────────────────────────────────────
// Persistent red vignette at screen edges that PULSES as HP drops below 30%.
// Intensifies into desaturation territory below 15%. The kind of peripheral
// dread that makes you scramble for a potion.

// Cached low-health warning gradients — rebuilt only on canvas resize
let _lhRedGrad: CanvasGradient | null = null;
let _lhDarkGrad: CanvasGradient | null = null;
let _lhW = 0;
let _lhH = 0;

function ensureLowHealthGrads(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  if (_lhRedGrad && _lhW === w && _lhH === h) return;
  const cx = w * 0.5;
  const cy = h * 0.5;
  const outerR = Math.sqrt(cx * cx + cy * cy);

  _lhRedGrad = ctx.createRadialGradient(cx, cy, outerR * 0.35, cx, cy, outerR);
  _lhRedGrad.addColorStop(0, "rgba(140, 20, 10, 0)");
  _lhRedGrad.addColorStop(0.6, "rgba(140, 20, 10, 0.3)");
  _lhRedGrad.addColorStop(1, "rgba(140, 20, 10, 1)");

  _lhDarkGrad = ctx.createRadialGradient(cx, cy, outerR * 0.25, cx, cy, outerR);
  _lhDarkGrad.addColorStop(0, "rgba(80, 10, 5, 0)");
  _lhDarkGrad.addColorStop(0.5, "rgba(80, 10, 5, 0.2)");
  _lhDarkGrad.addColorStop(1, "rgba(80, 10, 5, 1)");

  _lhW = w;
  _lhH = h;
}

function drawLowHealthWarning(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  canvasW: number,
  canvasH: number,
  now: number,
): void {
  const { combat } = state.player;
  if (combat.maxHp <= 0) return;

  const hpRatio = combat.hp / combat.maxHp;
  if (hpRatio >= 0.3) return; // no warning above 30%

  const critical = hpRatio < 0.15;

  // Pulse frequency: 1Hz normal, 1.5Hz critical
  const freq = critical ? 1.5 : 1.0;
  const pulse = Math.sin(now * Math.PI * 2 * freq) * 0.5 + 0.5; // 0→1 sinusoidal

  // Base alpha scales from 0.05 at 30% HP to 0.15 at 10% HP (linear)
  const alphaMin = 0.05;
  const alphaMax = 0.15;
  // Map hpRatio [0.3 → 0.1] to [0 → 1] intensity
  const intensity = clamp((0.3 - hpRatio) / 0.2, 0, 1);
  const baseAlpha = lerp(alphaMin, alphaMax, intensity);

  // Pulse modulates ±30% of base alpha
  const vignetteAlpha = baseAlpha * (0.7 + pulse * 0.3);

  ensureLowHealthGrads(ctx, canvasW, canvasH);

  ctx.save();
  // Red vignette — deep crimson at edges, transparent center
  ctx.globalAlpha = vignetteAlpha;
  ctx.fillStyle = _lhRedGrad!;
  ctx.fillRect(0, 0, canvasW, canvasH);

  // Critical: darker edges + slight grey desaturation at screen center
  if (critical) {
    // Darker vignette edges for impending doom
    ctx.globalAlpha = vignetteAlpha * 0.6;
    ctx.fillStyle = _lhDarkGrad!;
    ctx.fillRect(0, 0, canvasW, canvasH);

    // Slight desaturation — grey overlay at screen center (0.05 alpha, pulsing)
    const desatAlpha = 0.05 * (0.6 + pulse * 0.4);
    ctx.globalAlpha = desatAlpha;
    ctx.fillStyle = "#808080";
    ctx.fillRect(0, 0, canvasW, canvasH);
  }

  ctx.restore();
}

// ─── Combat Status Indicator ────────────────────────────────────────────────

function drawCombatIndicator(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  now: number,
): void {
  // Pulsing glow behind the text
  const pulse = 0.6 + Math.sin(now * 4) * 0.4;

  ctx.save();
  ctx.globalAlpha = pulse * 0.3;
  roundRect(ctx, x - 4, y - 2, 96, 22, 4);
  ctx.fillStyle = C.combatGlow;
  ctx.fill();
  ctx.restore();

  // Background pill
  roundRect(ctx, x - 4, y - 2, 96, 22, 4);
  ctx.fillStyle = C.woodDark;
  ctx.fill();
  roundRect(ctx, x - 4, y - 2, 96, 22, 4);
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Crossed swords and text
  ctx.save();
  ctx.font = "bold 11px monospace";
  ctx.fillStyle = C.combatText;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.globalAlpha = 0.7 + pulse * 0.3;
  ctx.fillText("\u2694 In Combat", x + 2, y + 2);
  ctx.restore();
}

// ─── Equipment Quick-View Strip ────────────────────────────────────────────
// Compact 4-slot gear readout beneath the health panel. Each slot shows a
// geometric icon colored by rarity. Total attack/defense bonuses on the right.

type SlotKey = "weapon" | "armor" | "accessory" | "shield";
const SLOT_ORDER: SlotKey[] = ["weapon", "armor", "accessory", "shield"];

/** Rarity → icon fill color */
function rarityColor(rarity: string | undefined): string {
  switch (rarity) {
    case "uncommon":  return C.rarUncommon;
    case "rare":      return C.rarRare;
    case "legendary": return C.rarLegendary;
    default:          return C.rarCommon;
  }
}

/** Draw a tiny weapon icon — diagonal blade with crossguard. */
function drawWeaponIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  // Blade — diagonal slash from top-right to bottom-left
  ctx.beginPath();
  ctx.moveTo(cx + 5, cy - 6);
  ctx.lineTo(cx - 4, cy + 5);
  ctx.stroke();
  // Crossguard — short horizontal line at the midpoint
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(cx - 2, cy - 1);
  ctx.lineTo(cx + 3, cy + 1);
  ctx.stroke();
  // Handle dot (pommel)
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx + 5, cy - 6, 1.2, 0, Math.PI * 2);
  ctx.fill();
}

/** Draw a tiny armor icon — chest plate with shoulder bumps. */
function drawArmorIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  // Torso rectangle
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy - 3);
  ctx.lineTo(cx + 4, cy - 3);
  ctx.lineTo(cx + 4, cy + 5);
  ctx.lineTo(cx - 4, cy + 5);
  ctx.closePath();
  ctx.stroke();
  // Left shoulder bump
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy - 3);
  ctx.lineTo(cx - 6, cy - 5);
  ctx.lineTo(cx - 6, cy - 1);
  ctx.lineTo(cx - 4, cy);
  ctx.stroke();
  // Right shoulder bump
  ctx.beginPath();
  ctx.moveTo(cx + 4, cy - 3);
  ctx.lineTo(cx + 6, cy - 5);
  ctx.lineTo(cx + 6, cy - 1);
  ctx.lineTo(cx + 4, cy);
  ctx.stroke();
  // Center detail line
  ctx.beginPath();
  ctx.moveTo(cx, cy - 3);
  ctx.lineTo(cx, cy + 2);
  ctx.stroke();
}

/** Draw a tiny accessory icon — gem circle with sparkle. */
function drawAccessoryIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  // Outer circle
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(cx, cy, 5, 0, Math.PI * 2);
  ctx.stroke();
  // Inner gem fill — small circle
  ctx.fillStyle = color;
  ctx.globalAlpha *= 0.7;
  ctx.beginPath();
  ctx.arc(cx, cy, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha /= 0.7;
  // Top sparkle tick
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx, cy - 6);
  ctx.lineTo(cx, cy - 7.5);
  ctx.stroke();
  // Right sparkle tick
  ctx.beginPath();
  ctx.moveTo(cx + 5, cy - 3);
  ctx.lineTo(cx + 6.5, cy - 4);
  ctx.stroke();
}

/** Draw a tiny shield icon — kite shape with boss rivet. */
function drawShieldIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineJoin = "round";
  // Kite shape
  ctx.beginPath();
  ctx.moveTo(cx, cy - 6);       // top
  ctx.lineTo(cx + 5, cy - 2);   // upper right
  ctx.lineTo(cx, cy + 6);       // bottom point
  ctx.lineTo(cx - 5, cy - 2);   // upper left
  ctx.closePath();
  ctx.stroke();
  // Center horizontal bar
  ctx.beginPath();
  ctx.moveTo(cx - 4, cy - 2);
  ctx.lineTo(cx + 4, cy - 2);
  ctx.stroke();
  // Center boss rivet
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy - 2, 1.5, 0, Math.PI * 2);
  ctx.fill();
}

/** Draw a dim "X" for an empty slot. */
function drawEmptySlotMark(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.strokeStyle = C.slotEmpty;
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(cx - 3, cy - 3);
  ctx.lineTo(cx + 3, cy + 3);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx + 3, cy - 3);
  ctx.lineTo(cx - 3, cy + 3);
  ctx.stroke();
}

// ─── Attunement Tier Gem ───────────────────────────────────────────────────
// A small colored gem rendered on the bottom-right corner of the weapon
// slot. The color reflects the current attunement tier (fresh → familiar →
// bonded → attuned → soulbound). Soulbound weapons glow softly.

type AttunementTierKey = "fresh" | "familiar" | "bonded" | "attuned" | "soulbound";

const ATTUNEMENT_TIER_COLORS: Record<AttunementTierKey, { fill: string; rim: string; glow: string }> = {
  fresh:     { fill: "#4a4038", rim: "#2a241c", glow: "rgba(120,100,80,0)" },
  familiar:  { fill: "#88a0c0", rim: "#385070", glow: "rgba(136,160,192,0.35)" },
  bonded:    { fill: "#88c088", rim: "#386038", glow: "rgba(136,192,136,0.4)" },
  attuned:   { fill: "#d4a030", rim: "#6b4f0c", glow: "rgba(212,160,48,0.5)" },
  soulbound: { fill: "#e87820", rim: "#6b3010", glow: "rgba(255,200,96,0.65)" },
};

const ATTUNEMENT_TIER_LABELS: Record<AttunementTierKey, string> = {
  fresh:     "Fresh",
  familiar:  "Familiar",
  bonded:    "Bonded",
  attuned:   "Attuned",
  soulbound: "Soulbound",
};

/**
 * Draw a small tier gem at (x, y) on the bottom-right of the slot.
 * Tier color maps to attunement progression. Soulbound weapons get a
 * subtle pulsing glow; attuned weapons have a steady warm aura.
 */
function drawAttunementGem(
  ctx: CanvasRenderingContext2D,
  x: number, y: number,
  tier: AttunementTierKey,
  level: number,
  now: number,
): void {
  const colors = ATTUNEMENT_TIER_COLORS[tier];
  const r = 3;

  ctx.save();

  // Pulsing glow for attuned/soulbound weapons — they breathe
  if (tier === "soulbound") {
    const pulse = 0.7 + Math.sin(now * Math.PI * 1.6) * 0.3;
    const glowR = 6 + pulse * 2;
    const g = ctx.createRadialGradient(x, y, 1, x, y, glowR);
    g.addColorStop(0, colors.glow);
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - glowR, y - glowR, glowR * 2, glowR * 2);
  } else if (tier === "attuned") {
    const g = ctx.createRadialGradient(x, y, 1, x, y, 5);
    g.addColorStop(0, colors.glow);
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - 5, y - 5, 10, 10);
  }

  // Gem body
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = colors.fill;
  ctx.fill();

  // Inner highlight for a gem-like specular
  ctx.beginPath();
  ctx.arc(x - 0.6, y - 0.8, 0.9, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255,255,255,0.45)";
  ctx.fill();

  // Rim
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.strokeStyle = colors.rim;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // Progress tick — a small arc around the gem showing progress within
  // the current tier (reads from the 0..1 level rescaled into the tier's
  // band). Visible only for bonded+ tiers.
  if (tier === "bonded" || tier === "attuned" || tier === "soulbound") {
    // Map the level into the within-tier fraction
    const bands: Record<AttunementTierKey, [number, number]> = {
      fresh:     [0.00, 0.20],
      familiar:  [0.20, 0.40],
      bonded:    [0.40, 0.60],
      attuned:   [0.60, 0.80],
      soulbound: [0.80, 1.00],
    };
    const [lo, hi] = bands[tier];
    const within = Math.max(0, Math.min(1, (level - lo) / Math.max(0.0001, hi - lo)));
    if (within > 0.02) {
      ctx.beginPath();
      ctx.arc(x, y, r + 1.2, -Math.PI / 2, -Math.PI / 2 + within * Math.PI * 2);
      ctx.strokeStyle = colors.fill;
      ctx.lineWidth = 0.9;
      ctx.stroke();
    }
  }

  ctx.restore();
}

function drawEquipmentStrip(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  mouseX: number,
  mouseY: number,
): void {
  const gear = state.player.equipment;

  // Slim wood panel background
  drawWoodPanel(ctx, EQ_STRIP_X, EQ_STRIP_Y, EQ_STRIP_W, EQ_STRIP_H, 4);

  // Compute total bonuses
  let totalAtk = 0;
  let totalDef = 0;
  for (let i = 0; i < SLOT_ORDER.length; i++) {
    const item = gear[SLOT_ORDER[i]];
    if (item?.equipmentData) {
      totalAtk += item.equipmentData.attackBonus;
      totalDef += item.equipmentData.defenseBonus;
    }
  }

  // Pull live attunement data — used to draw a small tier gem on the
  // weapon slot showing how bonded the player is with their weapon.
  const attunement = getAttunementData();
  const now = performance.now() / 1000;

  // Draw 4 equipment slots
  const slotsStartX = EQ_STRIP_X + 6;
  const slotY = EQ_STRIP_Y + EQ_SLOT_Y_OFF;

  // Track which slot is hovered (if any)
  let hoveredSlotKey: SlotKey | null = null;
  let hoveredItem: typeof gear.weapon = null;
  let hoveredSlotX = 0;

  for (let i = 0; i < SLOT_ORDER.length; i++) {
    const slotKey = SLOT_ORDER[i];
    const item = gear[slotKey];
    const sx = slotsStartX + i * (EQ_SLOT_SIZE + EQ_SLOT_GAP);

    // Check hover
    const isHovered =
      mouseX >= sx && mouseX <= sx + EQ_SLOT_SIZE &&
      mouseY >= slotY && mouseY <= slotY + EQ_SLOT_SIZE;

    // Inset background
    roundRect(ctx, sx, slotY, EQ_SLOT_SIZE, EQ_SLOT_SIZE, 3);
    ctx.fillStyle = isHovered ? "#1a1408" : C.slotBg;
    ctx.fill();

    // Thin gold border (brighter on hover)
    roundRect(ctx, sx, slotY, EQ_SLOT_SIZE, EQ_SLOT_SIZE, 3);
    ctx.strokeStyle = isHovered ? C.goldMid : C.slotBorder;
    ctx.lineWidth = isHovered ? 1.5 : 1;
    ctx.stroke();

    // Icon center
    const icx = sx + EQ_SLOT_SIZE * 0.5;
    const icy = slotY + EQ_SLOT_SIZE * 0.5;

    ctx.save();
    if (item) {
      const col = rarityColor(item.rarity);
      switch (slotKey) {
        case "weapon":    drawWeaponIcon(ctx, icx, icy, col);    break;
        case "armor":     drawArmorIcon(ctx, icx, icy, col);     break;
        case "accessory": drawAccessoryIcon(ctx, icx, icy, col); break;
        case "shield":    drawShieldIcon(ctx, icx, icy, col);    break;
      }
    } else {
      drawEmptySlotMark(ctx, icx, icy);
    }
    ctx.restore();

    // Attunement tier gem on the weapon slot
    if (slotKey === "weapon" && item) {
      const wa = attunement.weapons[item.name];
      if (wa) {
        drawAttunementGem(ctx, sx + EQ_SLOT_SIZE - 4, slotY + EQ_SLOT_SIZE - 4, wa.tier, wa.level, now);
      }
    }

    if (isHovered) {
      hoveredSlotKey = slotKey;
      hoveredItem = item;
      hoveredSlotX = sx;
    }
  }

  // Attack / Defense readout on the right
  const statsX = EQ_STRIP_X + EQ_STRIP_W - 8;
  const statsY = EQ_STRIP_Y + 8;

  ctx.save();
  ctx.textAlign = "right";
  ctx.textBaseline = "top";
  ctx.font = "bold 10px monospace";

  // Attack (sword line + number)
  ctx.fillStyle = C.atkColor;
  ctx.fillText(`\u2694 ${totalAtk}`, statsX, statsY - 3);

  // Defense (shield + number)
  ctx.fillStyle = C.defColor;
  ctx.fillText(`\u26e8 ${totalDef}`, statsX, statsY + 9);

  ctx.restore();

  // ── Equipment slot tooltip ──
  if (hoveredSlotKey) {
    // When hovering the weapon slot, pass along the attunement state so
    // the tooltip can display the current tier, progress, and bonuses.
    let attunementInfo: TooltipAttunementInfo | undefined;
    if (hoveredSlotKey === "weapon" && hoveredItem) {
      const wa = attunement.weapons[hoveredItem.name];
      if (wa) {
        attunementInfo = {
          tier: wa.tier as AttunementTierKey,
          level: wa.level,
          totalHits: wa.totalHits,
          stability: wa.stability,
        };
      }
    }
    drawEquipmentTooltip(ctx, hoveredSlotKey, hoveredItem, hoveredSlotX, slotY, attunementInfo);
  }
}

interface TooltipAttunementInfo {
  tier: AttunementTierKey;
  level: number;
  totalHits: number;
  stability: number;
}

/** Pretty slot label for tooltip header. */
const SLOT_LABELS: Record<SlotKey, string> = {
  weapon: "Weapon",
  armor: "Armor",
  accessory: "Accessory",
  shield: "Shield",
};

/** Draw a warm wood-panel tooltip below the equipment strip for a hovered slot. */
function drawEquipmentTooltip(
  ctx: CanvasRenderingContext2D,
  slotKey: SlotKey,
  item: import("../engine/state.js").InventoryItem | null,
  slotX: number,
  slotY: number,
  attunement?: TooltipAttunementInfo,
): void {
  const PAD_X = 12;
  const PAD_Y = 8;
  const LINE_H = 14;
  const SMALL_LINE_H = 13;
  const TIP_R = 6;

  ctx.save();

  // Build tooltip lines
  const nameFont  = "bold 11px monospace";
  const smallFont = "10px monospace";

  let lines: Array<{ text: string; font: string; color: string }> = [];

  if (item && item.equipmentData) {
    const eq = item.equipmentData;
    lines.push({ text: item.name, font: nameFont, color: "#f4e4b5" });
    lines.push({ text: SLOT_LABELS[slotKey], font: smallFont, color: "#a0855a" });

    // Stat bonuses — only show non-zero
    const stats: string[] = [];
    if (eq.attackBonus)  stats.push(`+${eq.attackBonus} Atk`);
    if (eq.defenseBonus) stats.push(`+${eq.defenseBonus} Def`);
    if (eq.hpBonus)      stats.push(`+${eq.hpBonus} HP`);
    if (stats.length > 0) {
      lines.push({ text: stats.join("  "), font: smallFont, color: C.goldBright });
    }

    // Level requirement
    if (eq.levelReq > 1) {
      lines.push({ text: `Requires Level ${eq.levelReq}`, font: smallFont, color: "#cc8844" });
    }

    // Description
    if (eq.description) {
      lines.push({ text: eq.description, font: smallFont, color: "#a0855a" });
    }

    // Attunement tier — shown only on the weapon slot. The tooltip becomes
    // the player's primary surface for understanding how bonded they've
    // become with their weapon, including the live damage/EP bonuses the
    // attunement is granting them right now.
    if (attunement) {
      const col = ATTUNEMENT_TIER_COLORS[attunement.tier].fill;
      lines.push({
        text: `◈ ${ATTUNEMENT_TIER_LABELS[attunement.tier]}  (${Math.round(attunement.level * 100)}%)`,
        font: smallFont,
        color: col,
      });
      const dmgBonus = attunement.level * attunement.level * 15; // matches getAttunementDamageBonus
      const epRed = attunement.level * 20;                       // matches getAttunementEPReduction
      const bonusBits: string[] = [];
      if (dmgBonus > 0.1) bonusBits.push(`+${dmgBonus.toFixed(1)}% dmg`);
      if (epRed > 0.1)    bonusBits.push(`-${epRed.toFixed(0)}% EP`);
      if (bonusBits.length > 0) {
        lines.push({ text: bonusBits.join("  "), font: smallFont, color: "#a0855a" });
      }
      lines.push({
        text: `${attunement.totalHits} hits  ·  stability ${attunement.stability.toFixed(0)}`,
        font: smallFont,
        color: "#807055",
      });
    }
  } else {
    lines.push({ text: `${SLOT_LABELS[slotKey]} — Empty`, font: nameFont, color: "#a0855a" });
  }

  // Measure max line width
  let maxW = 0;
  for (const l of lines) {
    ctx.font = l.font;
    const w = ctx.measureText(l.text).width;
    if (w > maxW) maxW = w;
  }

  const tipW = maxW + PAD_X * 2;
  const tipH = PAD_Y * 2 + LINE_H + Math.max(0, lines.length - 1) * SMALL_LINE_H;

  // Position: below the slot, horizontally centered on the slot
  let tipX = slotX + EQ_SLOT_SIZE / 2 - tipW / 2;
  const tipY = slotY + EQ_SLOT_SIZE + 6;

  // Clamp to not go off left/right edges of the equipment strip
  tipX = Math.max(EQ_STRIP_X, Math.min(tipX, EQ_STRIP_X + EQ_STRIP_W - tipW));

  // Drop shadow
  ctx.fillStyle = "rgba(10,6,0,0.5)";
  roundRect(ctx, tipX + 2, tipY + 2, tipW, tipH, TIP_R);
  ctx.fill();

  // Background — dark wood gradient
  roundRect(ctx, tipX, tipY, tipW, tipH, TIP_R);
  const grad = ctx.createLinearGradient(tipX, tipY, tipX, tipY + tipH);
  grad.addColorStop(0, "#2e2016");
  grad.addColorStop(0.5, "#241a10");
  grad.addColorStop(1, "#1a1208");
  ctx.fillStyle = grad;
  ctx.fill();

  // Gold border
  roundRect(ctx, tipX, tipY, tipW, tipH, TIP_R);
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Inner bevel highlight
  roundRect(ctx, tipX + 1.5, tipY + 1.5, tipW - 3, tipH - 3, TIP_R - 1);
  ctx.strokeStyle = "rgba(212,160,48,0.15)";
  ctx.lineWidth = 0.5;
  ctx.stroke();

  // Caret pointing up toward the slot
  const caretX = Math.max(tipX + 8, Math.min(slotX + EQ_SLOT_SIZE / 2, tipX + tipW - 8));
  ctx.fillStyle = "#1a1208";
  ctx.beginPath();
  ctx.moveTo(caretX - 5, tipY);
  ctx.lineTo(caretX, tipY - 5);
  ctx.lineTo(caretX + 5, tipY);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = C.goldDark;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(caretX - 5, tipY);
  ctx.lineTo(caretX, tipY - 5);
  ctx.lineTo(caretX + 5, tipY);
  ctx.stroke();

  // Draw text lines
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  let ty = tipY + PAD_Y;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    ctx.font = l.font;
    ctx.fillStyle = l.color;
    ctx.fillText(l.text, tipX + PAD_X, ty);
    ty += i === 0 ? LINE_H : SMALL_LINE_H;
  }

  ctx.restore();
}

// ─── Creature Health Bar ────────────────────────────────────────────────────

/** Draw a small 5-pointed star at (cx,cy) with given radius. */
function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, r: number,
): void {
  ctx.beginPath();
  for (let i = 0; i < 10; i++) {
    const angle = (i * Math.PI) / 5 - Math.PI / 2;
    const rad = i % 2 === 0 ? r : r * 0.45;
    const px = cx + Math.cos(angle) * rad;
    const py = cy + Math.sin(angle) * rad;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

const CREATURE_HIT_FLASH_DURATION = 0.15; // seconds

// Y-offset (in world pixels) from creature tile center to top of health bar, by species size
const CREATURE_SIZE_OFFSETS: Record<string, number> = { tiny: 30, small: 40, medium: 52, large: 68, huge: 88 };

// Per-creature smooth HP display state (cleared when combat ends or target changes)
interface CreatureDisplayHp {
  displayHp: number;   // lerps toward actual
  ghostHp: number;     // afterimage from last hit
  ghostAlpha: number;
  ghostStart: number;
}
const _creatureDisplayHp = new Map<string, CreatureDisplayHp>();

function drawCreatureHealthBar(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  camera: Camera,
  now: number,
): void {
  const { combatState } = state.player;
  if (!combatState.active || !combatState.targetId) return;

  const creature = state.creatures.find(c => c.id === combatState.targetId);
  if (!creature) return;

  const creatureHp = getCreatureHp(combatState.targetId);
  if (!creatureHp) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;
  const screen = toScreen(
    creature.position.tileX,
    creature.position.tileY,
    creature.position.elevation,
    camera, cw, ch,
  );

  // Detect hit flash — creature was recently struck
  const hitTime = state.creatureHitTimes.get(combatState.targetId) ?? 0;
  const hitAge = now - hitTime;
  const isHitFlashing = hitAge < CREATURE_HIT_FLASH_DURATION && hitTime > 0;
  const hitFlashT = isHitFlashing ? 1 - hitAge / CREATURE_HIT_FLASH_DURATION : 0;

  // Bar width widens briefly on hit (2px each side)
  const barExpand = isHitFlashing ? Math.round(hitFlashT * 2) : 0;
  const effectiveBarW = CREATURE_BAR_W + barExpand * 2;

  // Position bar above creature sprite — offset scales with species size
  const species = getSpecies(creature.speciesId);
  const yOffset = species ? (CREATURE_SIZE_OFFSETS[species.size] ?? 52) : 52;
  const barX = screen.x - effectiveBarW * 0.5;
  const barY = screen.y - yOffset * camera.zoom;

  // ── Danger level stars beside the name ──────────────────────────────
  const dangerLevel = getDangerLevel(state);

  // Name label + stars
  ctx.save();
  ctx.font = "bold 10px monospace";
  ctx.fillStyle = C.textPrime;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";

  // Text shadow for readability
  ctx.shadowColor = "rgba(0,0,0,0.7)";
  ctx.shadowBlur = 3;

  // Measure name to position stars after it
  const nameText = creature.name;
  const nameWidth = ctx.measureText(nameText).width;
  ctx.fillText(nameText, screen.x, barY - 3);
  ctx.restore();

  // Draw danger stars to the right of the name
  if (dangerLevel > 0) {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 2;
    const starStartX = screen.x + nameWidth * 0.5 + 4;
    const starY = barY - 8;
    for (let s = 0; s < dangerLevel; s++) {
      const sx = starStartX + s * 8;
      drawStar(ctx, sx, starY, 4);
      ctx.fillStyle = C.starColor;
      ctx.fill();
      // Dim outline for definition
      ctx.strokeStyle = C.goldDark;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }
    ctx.restore();
  }

  // ── Outer dark border for contrast ──────────────────────────────────
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  ctx.fillRect(barX - 2, barY - 2, effectiveBarW + 4, CREATURE_BAR_H + 4);

  // Bar background
  ctx.fillStyle = C.hpBg;
  ctx.fillRect(barX - 1, barY - 1, effectiveBarW + 2, CREATURE_BAR_H + 2);

  // Smooth creature HP animation — lerp + ghost bar
  const dt = lastFrameTime > 0 ? Math.max(0, now - lastFrameTime) : 0;
  let cdh = _creatureDisplayHp.get(combatState.targetId!);
  if (!cdh) {
    cdh = { displayHp: creatureHp.hp, ghostHp: creatureHp.hp, ghostAlpha: 0, ghostStart: 0 };
    _creatureDisplayHp.set(combatState.targetId!, cdh);
  }
  const prevCDisplay = cdh.displayHp;
  cdh.displayHp += (creatureHp.hp - cdh.displayHp) * Math.min(1, dt * 10);
  if (creatureHp.hp < prevCDisplay - 0.1) {
    cdh.ghostHp = cdh.ghostAlpha > 0.01 ? Math.max(cdh.ghostHp, prevCDisplay) : prevCDisplay;
    cdh.ghostAlpha = 1;
    cdh.ghostStart = now;
  }
  if (cdh.ghostAlpha > 0 && cdh.ghostStart > 0) {
    const gAge = now - cdh.ghostStart;
    cdh.ghostAlpha = gAge < GHOST_HP_DURATION ? 1 - gAge / GHOST_HP_DURATION : 0;
  }

  // Bar fill — red that darkens as HP drops, brighter on hit flash
  const ratio = clamp(cdh.displayHp / creatureHp.maxHp, 0, 1);
  const fillW = Math.round(effectiveBarW * ratio);

  // Ghost afterimage bar (behind fill)
  if (cdh.ghostAlpha > 0.01 && cdh.ghostHp > cdh.displayHp) {
    const ghostR = clamp(cdh.ghostHp / creatureHp.maxHp, 0, 1);
    const ghostFW = Math.round(effectiveBarW * ghostR);
    if (ghostFW > fillW) {
      ctx.save();
      ctx.globalAlpha = cdh.ghostAlpha * 0.4;
      ctx.fillStyle = "#a83020";
      ctx.fillRect(barX + fillW, barY, ghostFW - fillW, CREATURE_BAR_H);
      ctx.restore();
    }
  }

  if (fillW > 0) {
    const brightnessBoost = hitFlashT * 40; // flash brighter on hit
    const r = Math.min(255, Math.round(lerp(74, 200, ratio) + brightnessBoost));
    const g = Math.min(255, Math.round(lerp(16, 48, ratio) + brightnessBoost * 0.3));
    const b = Math.min(255, Math.round(lerp(16, 48, ratio) + brightnessBoost * 0.2));
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    ctx.fillRect(barX, barY, fillW, CREATURE_BAR_H);

    // Specular highlight along top half of bar
    const specG = ctx.createLinearGradient(barX, barY, barX, barY + CREATURE_BAR_H * 0.5);
    specG.addColorStop(0, `rgba(255,255,255,${isHitFlashing ? 0.2 : 0.08})`);
    specG.addColorStop(1, "transparent");
    ctx.fillStyle = specG;
    ctx.fillRect(barX, barY, fillW, CREATURE_BAR_H * 0.5);
  }

  // Hit flash white overlay — brief bright pulse on the entire bar
  if (isHitFlashing) {
    ctx.save();
    ctx.globalAlpha = hitFlashT * 0.35;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(barX, barY, effectiveBarW, CREATURE_BAR_H);
    ctx.restore();
  }

  // Bar border — thin dark outline for definition
  ctx.strokeStyle = "#0a0604";
  ctx.lineWidth = 1;
  ctx.strokeRect(barX - 1, barY - 1, effectiveBarW + 2, CREATURE_BAR_H + 2);

  // Gold accent border
  ctx.strokeStyle = C.hpBorder;
  ctx.lineWidth = 0.5;
  ctx.strokeRect(barX - 1, barY - 1, effectiveBarW + 2, CREATURE_BAR_H + 2);

  // HP fraction text (only when close enough to read)
  if (camera.zoom >= 0.8) {
    ctx.save();
    ctx.font = "8px monospace";
    ctx.fillStyle = C.textDim;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.shadowColor = "rgba(0,0,0,0.6)";
    ctx.shadowBlur = 2;
    ctx.fillText(`${creatureHp.hp}/${creatureHp.maxHp}`, screen.x, barY + CREATURE_BAR_H + 2);
    ctx.restore();
  }
}

// ─── Damage Numbers ─────────────────────────────────────────────────────────

function drawDamageNumbers(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  camera: Camera,
  now: number,
): void {
  const numbers = state.player.combatState.damageNumbers;
  if (numbers.length === 0) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  for (let i = 0; i < numbers.length; i++) {
    const dn = numbers[i];
    const age = now - dn.createdAt;

    // Skip if expired (combat system cleans these up, but guard anyway)
    if (age >= DAMAGE_LIFETIME) continue;

    // The damage number x/y are already in world-screen-space (before camera).
    // Apply camera transform to position them on the canvas.
    // dn.x/y are already in world-screen-space — apply camera transform
    const sx = dn.x * camera.zoom + camera.x;
    const sy = dn.y * camera.zoom + camera.y;

    // Delegate to the enhanced renderer from ability_ui which handles
    // type-based colors, directional float, crit star bursts, and status text.
    drawEnhancedDamageNumber(
      ctx,
      dn.value,
      dn.damageType,
      dn.isCrit,
      dn.isPlayerDamage,
      age,
      sx,
      sy,
      dn.angle,
    );
  }
}

// ─── Level Up Flash ─────────────────────────────────────────────────────────

function drawLevelUpFlash(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  // Auto-detect level changes
  const currentLevel = state.player.combat.level;
  if (lastKnownLevel > 0 && currentLevel > lastKnownLevel) {
    levelUpFlashTime = now;
  }
  lastKnownLevel = currentLevel;

  const age = now - levelUpFlashTime;
  if (age > LEVELUP_DURATION || levelUpFlashTime === 0) return;

  const t = age / LEVELUP_DURATION;

  // Golden screen burst — first 0.4s of the level up, fading radial gold overlay
  if (t < 0.4) {
    const burstT = t / 0.4;
    const burstAlpha = 0.15 * (1 - burstT);
    ctx.save();
    ctx.globalAlpha = burstAlpha;
    const cw = state.canvasWidth;
    const ch = state.canvasHeight;
    const cx = cw * 0.5;
    const cy = ch * 0.5;
    const maxR = Math.sqrt(cx * cx + cy * cy);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR * (0.3 + burstT * 0.7));
    grad.addColorStop(0, "rgba(255, 220, 80, 0.8)");
    grad.addColorStop(0.4, "rgba(240, 200, 96, 0.3)");
    grad.addColorStop(1, "rgba(240, 200, 96, 0)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, cw, ch);
    ctx.restore();
  }

  // Rise and fade
  const rise = t * 30;
  const alpha = t < 0.2
    ? t / 0.2                           // fade in
    : t > 0.6
      ? 1 - (t - 0.6) / 0.4            // fade out
      : 1;                              // fully visible

  // Scale pulse at the start
  const scale = t < 0.15
    ? 1 + (1 - t / 0.15) * 0.3         // scale down from 1.3 to 1.0
    : 1;

  // Position above the player health panel
  const x = PANEL_X + PANEL_W * 0.5;
  const y = PANEL_Y - 10 - rise;

  ctx.save();
  ctx.globalAlpha = clamp(alpha, 0, 1);
  ctx.translate(x, y);
  ctx.scale(scale, scale);

  // Glow
  ctx.shadowColor = C.levelUpGlow;
  ctx.shadowBlur = 16;

  ctx.font = "bold 18px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = C.levelUpText;

  // Draw twice for stronger glow
  ctx.fillText("Level Up!", 0, 0);
  ctx.fillText("Level Up!", 0, 0);

  // Sub-text with new level
  ctx.font = "12px monospace";
  ctx.shadowBlur = 8;
  ctx.fillStyle = C.goldBright;
  ctx.fillText(`Lv. ${currentLevel}`, 0, 18);

  ctx.restore();
}

// ─── Aggro Indicator ────────────────────────────────────────────────────────
// The "!" moment — when a hostile creature spots you and your heart jumps.
// Phase 1 (0–0.3s): "!" scales up from 0 to 1.2x with a bounce, bright red-orange
// Phase 2 (0.3–0.8s): "!" holds at 1.0x, subtle pulse
// Phase 3 (0.8–1.2s): fade out

const AGGRO_DURATION = 1.2; // total lifetime in seconds

function drawAggroIndicators(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  camera: Camera,
  now: number,
): void {
  const indicators = state.aggroIndicators;
  if (indicators.length === 0) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  // Clean up expired indicators
  for (let i = indicators.length - 1; i >= 0; i--) {
    if (now - indicators[i].time >= AGGRO_DURATION) {
      indicators.splice(i, 1);
    }
  }

  ctx.save();

  for (const indicator of indicators) {
    const age = now - indicator.time;
    if (age >= AGGRO_DURATION) continue;

    // Find the creature this indicator belongs to
    const creature = state.creatures.find(c => c.id === indicator.creatureId);
    if (!creature) continue;

    // Project creature position to screen
    const screen = toScreen(
      creature.position.tileX,
      creature.position.tileY,
      creature.position.elevation,
      camera, cw, ch,
    );

    // Position above creature's head
    const baseY = screen.y - 60 * camera.zoom;

    // Phase calculations
    let scale: number;
    let alpha: number;
    let offsetY: number;

    if (age < 0.3) {
      // Phase 1: bounce in — elastic ease-out from 0 to 1.2 then settle
      const t = age / 0.3;
      // Overshoot curve: shoots past 1.0 then springs back
      const overshoot = Math.sin(t * Math.PI) * 0.3;
      scale = t * (1.0 + overshoot);
      // Pop upward then settle
      offsetY = (1 - t) * -8;
      alpha = Math.min(1, t * 3); // rapid fade-in
    } else if (age < 0.8) {
      // Phase 2: hold at 1.0x with a subtle pulse
      const t = (age - 0.3) / 0.5;
      const pulse = 1.0 + Math.sin(t * Math.PI * 4) * 0.05;
      scale = pulse;
      offsetY = 0;
      alpha = 1;
    } else {
      // Phase 3: fade out, drift upward slightly
      const t = (age - 0.8) / 0.4;
      scale = 1.0 - t * 0.2;
      offsetY = -t * 6;
      alpha = 1 - t;
    }

    const drawX = screen.x;
    const drawY = baseY + offsetY;

    ctx.save();
    ctx.globalAlpha = clamp(alpha, 0, 1);
    ctx.translate(drawX, drawY);
    ctx.scale(scale * camera.zoom, scale * camera.zoom);

    // Outer glow — warm orange halo
    ctx.shadowColor = "rgba(255, 120, 30, 0.8)";
    ctx.shadowBlur = 16;

    // Draw the "!" — bold, chunky, impossible to miss
    ctx.font = "bold 28px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Dark outline for readability against any background
    ctx.strokeStyle = "rgba(20, 8, 0, 0.9)";
    ctx.lineWidth = 4;
    ctx.lineJoin = "round";
    ctx.strokeText("!", 0, 0);

    // Red-orange fill — the color of danger
    const fillGrad = ctx.createLinearGradient(0, -12, 0, 12);
    fillGrad.addColorStop(0, "#ff6020");
    fillGrad.addColorStop(0.5, "#ff4010");
    fillGrad.addColorStop(1, "#cc2800");
    ctx.fillStyle = fillGrad;
    ctx.fillText("!", 0, 0);

    // Hot center highlight — makes it feel luminous
    ctx.shadowBlur = 0;
    ctx.globalAlpha = clamp(alpha * 0.4, 0, 1);
    ctx.fillStyle = "#ffcc60";
    ctx.fillText("!", 0, 0);

    ctx.restore();
  }

  ctx.restore();
}

// ─── Combat Session Stats Overlay ────────────────────────────────────────
// Compact panel showing real-time DPS, combat timer, and avoidance stats
// during active combat. Kill streak banner shown over center of screen.

function drawCombatSessionStats(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const { combatState } = state.player;
  if (!combatState.active) return;

  const ss = combatState.sessionStats;
  const combatDuration = ss.combatStartedAt > 0 ? now - ss.combatStartedAt : 0;

  // Position: below the equipment strip
  const panelX = PANEL_X;
  const panelY = EQ_STRIP_Y + EQ_STRIP_H + 4;
  const panelW = 200;
  const panelH = 36;

  // Semi-transparent wood panel
  ctx.save();
  ctx.globalAlpha = 0.85;
  drawWoodPanel(ctx, panelX, panelY, panelW, panelH, 4);
  ctx.restore();

  // Combat timer — MM:SS format
  const mins = Math.floor(combatDuration / 60);
  const secs = Math.floor(combatDuration % 60);
  const timerText = `${mins}:${secs.toString().padStart(2, "0")}`;

  ctx.save();
  ctx.font = "9px monospace";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  // Timer
  ctx.fillStyle = C.textDim;
  ctx.fillText(`⏱ ${timerText}`, panelX + 6, panelY + 4);

  // DPS
  ctx.fillStyle = ss.currentDps > 0 ? C.goldBright : C.textDim;
  ctx.fillText(`DPS: ${ss.currentDps.toFixed(1)}`, panelX + 60, panelY + 4);

  // Total damage dealt
  ctx.fillStyle = C.textDim;
  ctx.fillText(`Dmg: ${ss.totalDamageDealt}`, panelX + 130, panelY + 4);

  // Avoidance stats (second row)
  ctx.fillStyle = "#80c0e0";
  ctx.fillText(`D:${ss.dodgeCount}`, panelX + 6, panelY + 18);
  ctx.fillStyle = "#d0a040";
  ctx.fillText(`P:${ss.parryCount}`, panelX + 40, panelY + 18);
  ctx.fillStyle = "#8888aa";
  ctx.fillText(`B:${ss.blockCount}`, panelX + 74, panelY + 18);

  // Kills
  ctx.fillStyle = "#60e060";
  ctx.fillText(`Kills: ${ss.totalKills}`, panelX + 110, panelY + 18);

  // Kill streak (if active)
  if (ss.killStreak >= 2) {
    ctx.fillStyle = ss.killStreak >= 5 ? C.comboRed : ss.killStreak >= 3 ? C.comboOrange : C.comboGold;
    ctx.fillText(`🔥 ${ss.killStreak}`, panelX + 170, panelY + 18);
  }

  ctx.restore();
}

// ─── Kill Streak Banner ─────────────────────────────────────────────────────
// Giant center-screen banner for epic kill streaks (3+). Fades in, holds, fades out.

function drawKillStreakBanner(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const ss = state.player.combatState.sessionStats;
  if (ss.killStreakShowTime <= 0) return;

  const cw = state.canvasWidth;
  const ch = state.canvasHeight;

  // Banner progress: starts at 3.0, counts down to 0
  const remaining = ss.killStreakShowTime;
  const total = 3.0;
  const t = 1 - remaining / total; // 0→1

  // Alpha: fade in 0→0.15, hold 0.15→0.7, fade out 0.7→1.0
  let alpha: number;
  if (t < 0.15) alpha = t / 0.15;
  else if (t < 0.7) alpha = 1;
  else alpha = 1 - (t - 0.7) / 0.3;

  // Scale: slight bounce-in
  const scale = t < 0.1
    ? 0.8 + 0.3 * (t / 0.1)  // start at 0.8, overshoot to 1.1
    : t < 0.2
      ? 1.1 - 0.1 * ((t - 0.1) / 0.1)  // settle to 1.0
      : 1.0;

  const streakNames = ["", "", "", "Killing Spree", "Rampage", "Unstoppable", "Godlike", "Legendary"];
  const streakName = streakNames[Math.min(ss.killStreak, streakNames.length - 1)] || `${ss.killStreak}x Streak`;

  ctx.save();
  ctx.globalAlpha = clamp(alpha, 0, 1);
  ctx.translate(cw * 0.5, ch * 0.35);
  ctx.scale(scale, scale);

  // Glow
  const glowColor = ss.killStreak >= 6 ? "#ff4020" : ss.killStreak >= 4 ? "#ff8820" : "#ffd040";
  ctx.shadowColor = glowColor;
  ctx.shadowBlur = 20;

  // Text
  ctx.font = "bold 28px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Dark outline
  ctx.strokeStyle = "rgba(0,0,0,0.8)";
  ctx.lineWidth = 4;
  ctx.lineJoin = "round";
  ctx.strokeText(streakName, 0, 0);

  // Colored fill
  ctx.fillStyle = glowColor;
  ctx.fillText(streakName, 0, 0);

  // Hot center
  ctx.globalAlpha = clamp(alpha * 0.4, 0, 1);
  ctx.fillStyle = "#ffffff";
  ctx.fillText(streakName, 0, 0);

  // Streak count below
  ctx.globalAlpha = clamp(alpha * 0.8, 0, 1);
  ctx.font = "bold 16px monospace";
  ctx.shadowBlur = 10;
  ctx.fillStyle = C.goldGlow;
  ctx.strokeText(`${ss.killStreak} kills`, 0, 26);
  ctx.fillText(`${ss.killStreak} kills`, 0, 26);

  ctx.restore();
}

// ─── Damage Vignette ───────────────────────────────────────────────────────
// A blood-red radial overlay that flashes when the player takes damage.
// Appears instantly, fades over 400ms. Subtle enough to not obscure gameplay,
// visceral enough to make every hit register in your gut.

const VIGNETTE_DURATION = 0.4; // seconds
const VIGNETTE_PEAK_ALPHA = 0.25;

// Cached damage vignette gradient — rebuilt only on canvas resize
let _dvGrad: CanvasGradient | null = null;
let _dvW = 0;
let _dvH = 0;

function getDamageVignetteGrad(ctx: CanvasRenderingContext2D, w: number, h: number): CanvasGradient {
  if (_dvGrad && _dvW === w && _dvH === h) return _dvGrad;
  const cx = w * 0.5;
  const cy = h * 0.5;
  const outerRadius = Math.sqrt(cx * cx + cy * cy);
  _dvGrad = ctx.createRadialGradient(cx, cy, outerRadius * 0.3, cx, cy, outerRadius);
  _dvGrad.addColorStop(0, "rgba(180, 30, 20, 0)");
  _dvGrad.addColorStop(1, "rgba(180, 30, 20, 1)");
  _dvW = w;
  _dvH = h;
  return _dvGrad;
}

function drawDamageVignette(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  now: number,
): void {
  const hitTime = state.lastPlayerHitTime;
  if (hitTime <= 0) return;

  const age = now - hitTime;
  if (age >= VIGNETTE_DURATION) return;

  // Instant onset, linear fade
  const alpha = VIGNETTE_PEAK_ALPHA * (1 - age / VIGNETTE_DURATION);
  if (alpha <= 0) return;

  const w = state.canvasWidth;
  const h = state.canvasHeight;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.fillStyle = getDamageVignetteGrad(ctx, w, h);
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

// ─── Main Entry Point ───────────────────────────────────────────────────────

/**
 * Draw all combat-related UI overlays.
 *
 * Called every frame by the room renderer. Draws on top of the room scene
 * but beneath any modal UI (inventory, chat input, etc.).
 *
 * @param ctx       The 2D canvas rendering context
 * @param state     Current game state
 * @param camera    Camera with x, y, zoom (iso/projection Camera interface)
 * @param timestamp Current time in seconds (performance.now() / 1000)
 */
export function drawCombatUI(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  camera: Camera,
  timestamp: number,
  mouseX = 0,
  mouseY = 0,
): void {
  const dangerLevel = getDangerLevel(state);
  const inCombat = state.player.combatState.active;

  // Player health bar — visible in any danger zone, combat, or combat stance
  const showPanel = dangerLevel > 0 || inCombat || state.ui.combatStance;
  if (showPanel) {
    drawPlayerHealthBar(ctx, state, timestamp);

    // Momentum meter — the primary combat-flow visualization. Replaces the
    // old discrete combo counter with a living ODE readout (arc fill,
    // fatigue ceiling, flow-threshold notch, peak tick, inner heartbeat
    // trace). The combo number is embedded in the meter's center so the
    // discrete count is still visible.
    const dt = lastFrameTime > 0 ? Math.max(0, timestamp - lastFrameTime) : 0;
    const momentum = getMomentumState();
    // The meter sits to the right of the combat-status pill so both can
    // coexist. When not in combat, the pill is absent and the meter
    // stands alone in a dim resting state.
    drawMomentumMeter(ctx, momentum, _momentumTel, {
      cx: PANEL_X + PANEL_W + 8 + 96 + 8 + 30,
      cy: PANEL_Y + PANEL_H * 0.5,
      outerR: 30,
      combo: state.player.combatState.combo,
      dt,
      now: timestamp,
      active: inCombat,
    });

    // Equipment quick-view — compact gear strip beneath the health panel
    drawEquipmentStrip(ctx, state, mouseX, mouseY);
  }

  // Combat session stats — DPS meter, timer, avoidance, kill count
  if (inCombat) {
    drawCombatSessionStats(ctx, state, timestamp);
  }

  // Creature health bar — only when actively fighting (now with danger stars + hit flash)
  if (inCombat) {
    drawCreatureHealthBar(ctx, state, camera, timestamp);
  } else if (_creatureDisplayHp.size > 0) {
    _creatureDisplayHp.clear();
  }

  // Floating damage numbers — always rendered if present (they linger after combat ends)
  if (state.player.combatState.damageNumbers.length > 0) {
    drawDamageNumbers(ctx, state, camera, timestamp);
  }

  // Aggro indicators — the dramatic "!" when you've been spotted
  if (state.aggroIndicators.length > 0) {
    drawAggroIndicators(ctx, state, camera, timestamp);
  }

  // Kill streak banner — giant center-screen text for 3+ streaks
  drawKillStreakBanner(ctx, state, timestamp);

  // Level up flash — triggered by level changes, fades over time
  drawLevelUpFlash(ctx, state, timestamp);

  // Damage vignette — red flash overlay when the player takes a hit
  drawDamageVignette(ctx, state, timestamp);

  // Low health warning — persistent pulsing vignette, drawn AFTER damage vignette
  // so they layer naturally (damage flash on top of the persistent dread)
  drawLowHealthWarning(ctx, state, state.canvasWidth, state.canvasHeight, timestamp);

  // Update frame time tracker for dt calculations
  lastFrameTime = timestamp;
}
